import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import type {
  AdsElementType,
  AdsEventInfo,
  AdsOptions,
  AppState,
} from '@jobseeker/common-core/types';
import {getUbaPageName} from '@jobseeker/common-core/utils';
import {
  getQueryParamsAndSeekerId,
  openUrl,
} from '@jobseeker/common-core/utils/common';
import {useInView} from '@jobseeker/lotus/hooks/observer';
import {Box, Card, Skeleton, Typography} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useRouter} from 'next/router';
import {useEffect, useMemo, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';

import {handleTracking, TrackEventUBA} from '@jobseeker/common-core/utils';
import {getRandomElementFromArray} from '@jobseeker/common-core/utils/common';
import {getRefEmbedded} from '@jobseeker/common-core/utils/refTrack';

type FeaturedInstituteProps = {
  slot: string;
  label?: string;
  options?: AdsOptions;
  titleOut?: boolean;
  type?: string;
  adsEventInfo?: AdsEventInfo;
  maxAds?: number;
  companyBannerEvent?: boolean;
  fixHeight?: boolean;
};

const FeaturedInstitute = ({
  slot,
  label,
  options,
  adsEventInfo,
  fixHeight = true,
  titleOut = false,
  type = 'institute',
  maxAds = 2,
  companyBannerEvent = false,
}: FeaturedInstituteProps) => {
  const [featuredInstitues, setfeaturedInstitues] = useState<AdsElementType[]>(
    []
  );

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const [showSkeleton, setShowSkeleton] = useState(true);
  const [adsLoaded, setAdsLoaded] = useState(false);

  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory
  );
  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag,
    shallowEqual
  );

  const featuredInstituesInfo = useSelector(
    (state: AppState) => state.ads.featuredInstitues,
    shallowEqual
  );

  const eventInfo = useSelector((state: AppState) => state.ads.events);

  useLoadAds(
    slot,
    setfeaturedInstitues,
    {categoryIds: categoryId ? categoryId?.toString() : '', ...options},
    false,
    type === 'event'
      ? !eventInfo[0]?.imageUrl
      : !featuredInstituesInfo[0]?.imageUrl,
    setAdsLoaded
  );

  const featuredAdsInfo = useMemo(() => {
    return type === 'event' ? eventInfo : featuredInstituesInfo;
  }, [type]);

  const loadedFeaturedInstitutes = featuredAdsInfo[0]?.imageUrl
    ? featuredAdsInfo
    : featuredInstitues;

  let filteredFeaturedInstitutes = loadedFeaturedInstitutes;

  //show only jobfeed ads on non-category pages
  if (!categoryId) {
    filteredFeaturedInstitutes = loadedFeaturedInstitutes.filter(
      (ad: AdsElementType) => {
        return ad['allowOnJobfeed'] == '1';
      }
    );
  }

  const [randomFeaturedInstitutes, setRandomFeaturedInstitutes] = useState<
    Array<AdsElementType>
  >([]);

  useEffect(() => {
    if (
      randomFeaturedInstitutes.length === 0 &&
      Array.isArray(filteredFeaturedInstitutes) &&
      filteredFeaturedInstitutes.length > 0
    ) {
      setRandomFeaturedInstitutes(
        getRandomElementFromArray(
          filteredFeaturedInstitutes,
          maxAds
        ) as AdsElementType[]
      );
    }
  }, [filteredFeaturedInstitutes, maxAds]);

  const {ref, isInView} = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const [hasTracked, setHasTracked] = useState(false);

  useEffect(() => {
    if (
      hasTracked ||
      !isInView ||
      featuredInstitues.length === 0 ||
      !randomFeaturedInstitutes?.length
    ) {
      return;
    }

    const source = undefined;

    if (type === 'institute') {
      TrackEventUBA('featuredInstituteView', 'view', {
        categoryID: page === 'category' ? categoryId : undefined,
        tagID: page === 'tag' ? Number(tagId) : undefined,
        page: getUbaPageName(),
        source,
        sessionUsername: sessionStorage.getItem('userId') || '',
        ref: new URL(window.location.href).searchParams.get('ref') || '',
        featuredInstituteId: randomFeaturedInstitutes.map(item => item.id),
        featuredInstituteName: randomFeaturedInstitutes.map(item => item.title),
      });
    }

    if (type === 'event') {
      const ref = new URL(window.location.href).searchParams.get('ref') || '';
      const pageName = getUbaPageName();
      const sessionUsername = sessionStorage.getItem('userId') || '';
      const categoryID = page === 'category' ? categoryId : undefined;
      const tagID = page === 'tag' ? Number(tagId) : undefined;

      const companyIdMap: Record<string, string> = {
        intuit: '1379',
        cgi: '380',
        amex: '40',
      };

      randomFeaturedInstitutes.forEach(item => {
        const titleKey = item.title.toLowerCase().trim();
        const companyId = companyIdMap[titleKey] || '';

        if (companyBannerEvent) {
          TrackEventUBA('companyBannerView', 'view', {
            ref,
            page: pageName,
            categoryID,
            tagID,
            companyId: item?.companyId ?? companyId,
            companyName: item.title,
            sessionUsername,
          });
        } else {
          TrackEventUBA('adBannerView', 'view', {
            ref,
            page: pageName,
            categoryID,
            tagID,
            companyId: item?.companyId ?? companyId,
            companyName: item.title,
            sessionUsername,
          });
        }
      });
    }

    setHasTracked(true);
  }, [isInView, featuredInstitues, hasTracked, randomFeaturedInstitutes]);

  const showCardFrame =
    type === 'event' && !loadedFeaturedInstitutes?.length ? false : true;

  const page = useSelector((state: AppState) => state.app.page, shallowEqual);

  const router = useRouter();
  const trackEvent = (adItem: {
    courseRefCode: number | string;
    id?: string | number;
    link: string;
    courseId?: string;
    title?: string;
    companyId?: number;
  }) => {
    let eventName = 'courseClick';
    let source: string | undefined;

    const eventData: Record<string, any> = {
      ref: new URL(window.location.href).searchParams.get('ref') || '',
      categoryID: page === 'category' ? categoryId : undefined,
      tagID: page === 'tag' ? Number(tagId) : undefined,
      page: getUbaPageName(),
      courseID: adItem.courseId || '',
      refInv:
        getQueryParamsAndSeekerId(adItem.link).queryParams['refinv'] || '',
      refOrg:
        getQueryParamsAndSeekerId(adItem.link).queryParams['reforg'] || '',
      courseRefCode: router.query.ref || '',
      sessionUsername: sessionStorage.getItem('userId') || '',
    };
    if (label?.toLowerCase()?.includes('institute')) {
      eventName = 'featuredInstituteClick';
      eventData.featuredInstituteId = adItem.id || '';
      eventData.featuredInstituteName = adItem.title || '';
    }
    if (type === 'event') {
      eventName = 'adBannerClick';
      eventData.companyId =
        adItem?.companyId ??
        (adItem.title === 'CGI'
          ? '380'
          : adItem.title === 'AMEX'
          ? '40'
          : adItem.id || '');
      eventData.companyName = adItem.title || '';
    }
    if (source) {
      eventData.source = source;
    }
    if (companyBannerEvent) {
      TrackEventUBA('companyBannerClick', 'click', eventData);
    } else {
      TrackEventUBA(eventName, 'click', eventData);
    }
  };

  const callEvent = (type: 'view' | 'click') => {
    if (adsEventInfo && adsEventInfo[type])
      handleTracking(adsEventInfo[type], {
        event_category: adsEventInfo['eventCategory'],
        AdTitle: loadedFeaturedInstitutes[0]?.title,
        Origin: page,
        Source: 'FeaturedBanner',
        Type: type,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Ref: 'logo',
        Refinv: 'featured',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
        categoryId,
      });
  };

  useEffect(() => {
    setShowSkeleton(false);
    callEvent('view');
  }, [loadedFeaturedInstitutes]);

  return (
    <>
      {adsLoaded ? (
        showCardFrame &&
        randomFeaturedInstitutes &&
        randomFeaturedInstitutes?.length > 0 && (
          <>
            {titleOut && label && loadedFeaturedInstitutes?.length > 0 && (
              <Typography
                fontSize={16}
                fontWeight={500}
                color="secondary.main"
                sx={{mb: 1.5}}
              >
                {label}
              </Typography>
            )}

            <Card
              data-testid={`job_ads_banner_${label
                ?.toLowerCase()
                ?.replace(/ /g, '-')}`}
              id={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
              sx={{
                borderRadius: titleOut ? 2 : 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: 1.5,
                px: 1.5,
                py: 2,
                my: 2,
                mb: titleOut ? 3.75 : 0,
                '@media (max-width: 320px)': {
                  display: 'none',
                },
                ...(!isAppIimjobs && type === 'event' && {gap: 0}),
              }}
            >
              {!titleOut && label && loadedFeaturedInstitutes?.length > 0 && (
                <Typography lineHeight={'21px'} variant="subtitle2">
                  {label}
                </Typography>
              )}

              <Box
                ref={ref}
                width={'100%'}
                display={'flex'}
                flexDirection={'column'}
                gap={1.5}
                justifyContent={'center'}
                alignItems={'center'}
              >
                {randomFeaturedInstitutes?.map((item, i: number) => (
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      width: 300,
                      height: titleOut ? 100 : !fixHeight ? 'auto' : 250,
                      '@media (width: 1024px)': {
                        width: '100%',
                        height: !fixHeight ? 'auto' : '207px',
                      },
                      position: 'relative',
                    }}
                    key={`ads-featured-institute-${i}`}
                  >
                    <Image
                      src={item['imageUrl']}
                      alt="MIT"
                      quality={50}
                      placeholder="blur"
                      blurDataURL={item['imageUrl']}
                      onClick={event => {
                        event?.preventDefault();
                        setTimeout(() => {
                          if (
                            item?.['link'].trim().length &&
                            item?.['link'].trim() !== '#'
                          ) {
                            openUrl(
                              companyBannerEvent
                                ? item?.['link']
                                : `${getRefEmbedded(
                                    item?.['link'],
                                    'FEATURED_INSTITUTES',
                                    'normal'
                                  )}`,
                              '_blank'
                            );
                          }
                        }, 0);
                        callEvent('click');
                        trackEvent(item);
                      }}
                      {...(!fixHeight
                        ? {
                            width: 300,
                            height: 250,
                            style: {
                              width: '100%',
                              height: 'auto',
                              objectFit: 'contain',
                              cursor: item?.['link'].trim().length
                                ? 'pointer'
                                : 'default',
                            },
                          }
                        : {
                            fill: true,
                            style: {
                              objectFit: 'fill',
                              cursor: item?.['link'].trim().length
                                ? 'pointer'
                                : 'default',
                            },
                          })}
                    />
                  </Box>
                ))}
              </Box>
            </Card>
          </>
        )
      ) : (
        <Card
          data-testid={`job_ads_banner_${label
            ?.toLowerCase()
            ?.replace(/ /g, '-')}`}
          id={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
          sx={{
            borderRadius: titleOut ? 2 : 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: 1.5,
            px: 1.5,
            py: 2,
            my: 2,
            mb: titleOut ? 3.75 : 0,
            '@media (max-width: 320px)': {
              display: 'none',
            },
          }}
        >
          <>
            {type !== 'event' && (
              <Skeleton
                variant="rounded"
                width={'50%'}
                height={21}
                sx={{
                  mt: {xxs: 0, md: 1},
                  ml: {xxs: 2, md: 'none'},
                  mb: 1,
                }}
              />
            )}
            {[...new Array(type === 'event' ? 1 : 2)].map(index => {
              return (
                <Skeleton
                  variant="rounded"
                  width={'94%'}
                  height={250}
                  key={`adsbanner-skele-${index}`}
                  sx={{
                    mt: {xxs: 0, md: 2},
                    ml: {xxs: 2, md: 'none'},
                    mb: 1,
                    '@media (width: 1024px)': {
                      width: '100%',
                      height: '207px',
                    },
                  }}
                />
              );
            })}
          </>
        </Card>
      )}
    </>
  );
};

export default FeaturedInstitute;
